"use client"
import styled from "@emotion/styled"
import { Button } from "@mui/material"
import { usePathname } from "next/navigation"
import { FC, useRef } from "react"
import { extractProperty } from "utils"
import { FetchMenusQuery, GetFootersQuery } from "../../apollo"
import { Desktop, useIsDesktop } from "../common/bit/MediaQueries"
import Analytics from "./Analytics"
import Footer from "./Footer/Footer"
import Header from "./Header/Header"
// Conditional import for telemetry components (only in egged app)
let TelemetryProvider: any = null
let TelemetryErrorBoundary: any = null

try {
  // These imports will only work in the egged app
  const telemetryModule = require("../../../apps/egged/components/TelemetryProvider")
  const errorBoundaryModule = require("../../../apps/egged/components/TelemetryErrorBoundary")
  TelemetryProvider = telemetryModule.TelemetryProvider
  TelemetryErrorBoundary = errorBoundaryModule.TelemetryErrorBoundary
} catch {
  // Fallback components for other apps
  TelemetryProvider = ({ children }: { children: React.ReactNode }) => (
    <>{children}</>
  )
  TelemetryErrorBoundary = ({ children }: { children: React.ReactNode }) => (
    <>{children}</>
  )
}

const StyledButton = styled(Button)`
  position: fixed;
  z-index: -10;
  background-color: black;
  color: white;
  top: -100px;
  &:focus-visible {
    z-index: 9999999;
    top: 0px;
  }
`
const StyledMain = styled.div<{
  isHomepage: boolean
  noPadding?: boolean
  fullHeight?: boolean
}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: ${(p) => (p.fullHeight ? "100%" : "")};
  width: 100%;
  flex: 1;
  padding-top: ${(p) => (p.noPadding ? 0 : "64px")};
  ${(p) =>
    !p.noPadding &&
    `${p.theme.breakpoints.up("md")} {
      padding-top: 80px;
    }`}
  :focus {
    outline: 0;
  }
`

const EmbedFooterSpace = styled.div`
  height: 200px;
`

type UndStr = string | undefined

function getHeaderData(header: any) {
  const siteLogo = extractProperty(header, "siteLogo") as UndStr
  const contactLogo = extractProperty(header, "contactLogo") as UndStr
  const contactUs = extractProperty(header, "contactUs")
  const contactTitle = extractProperty(contactUs?.[0], "title") as UndStr
  const contactItemsList = extractProperty(contactUs?.[0], "itemsList") || []
  const contactSubtitle = extractProperty(contactUs?.[0], "subtitle") as UndStr
  const itemsList = contactItemsList.map((el: any) => ({
    icon: extractProperty(el, "icon"),
    link: extractProperty(el, "link")?.[0]?.url,
    label: extractProperty(el, "link")?.[0]?.name,
  })) as {
    icon: string
    link: string
    label: string
  }[]
  return { siteLogo, contactLogo, contactTitle, itemsList, contactSubtitle }
}

export type HeaderData = ReturnType<typeof getHeaderData>
export interface LayoutProps {
  locale: string
  children: React.ReactNode
  menu_data: FetchMenusQuery
  footer_data: GetFootersQuery
}

const Layout: FC<LayoutProps> = ({
  locale,
  children,
  menu_data,
  footer_data,
}) => {
  const mainRef = useRef<HTMLDivElement>(null)
  const pathName = usePathname()
  const desktopHeader = getHeaderData(footer_data.header)
  const mobileHeader = getHeaderData(footer_data.headerMobile)
  const isDesktop = useIsDesktop()
  const isEmbedded = pathName?.startsWith("/embed") || false
  const isLanding = pathName?.startsWith("/landing")
  const handleClick = () => {
    if (mainRef.current) {
      mainRef.current.focus()
    }
  }
  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <TelemetryErrorBoundary>
        <TelemetryProvider>
          <Analytics />
          {!isEmbedded && !isLanding ? (
            <Header
              locale={locale}
              menu_data={menu_data}
              header={isDesktop ? desktopHeader : mobileHeader}
            />
          ) : null}
          <Desktop>
            <StyledButton
              id="skip-to-content"
              tabIndex={3}
              onClick={handleClick}
            >
              {locale === "he" ? "דלג לתוכן המרכזי" : "skip navigation"}
            </StyledButton>
          </Desktop>
          {/* Main content area */}
          <StyledMain
            id="mainContent"
            tabIndex={-1}
            ref={mainRef}
            role="main"
            isHomepage={pathName === "/"}
            data-egged-main
            fullHeight={isLanding}
            noPadding={isEmbedded || isLanding} // No padding if embedded
          >
            {children}
          </StyledMain>
          {!isEmbedded && !isLanding ? (
            <Footer footer_data={footer_data} />
          ) : null}
          {isEmbedded ? <EmbedFooterSpace /> : null}
        </TelemetryProvider>
      </TelemetryErrorBoundary>
    </div>
  )
}

export default Layout
